import { useBannerInboxMenu } from '@hub/app/banners';
import { PlayerLink } from '@hub/app/commons/static-links';
import { useAuth, useAuthUser } from '@hub/auth';
import { m } from '@hub/messages';
import { Link, login, useMatches } from '@hub/router';
import { useFeatureFlag } from '@platform/feature-flags';
import { Icon, SiteHeader } from '@platform/ui';
import { env } from '../../config/runtime';
import { WatchPaths } from '../../domains/watch/route.paths';

// Reads the leaf route's hideHeaderSupport; deeper matches win over layout parents.
function useHideHeaderSupport(): boolean {
  return useMatches({ select: (matches) => matches.at(-1)?.staticData?.hideHeaderSupport === true });
}

function useHideHeaderUser(): boolean {
  return useMatches({ select: (matches) => matches.at(-1)?.staticData?.hideHeaderUser === true });
}

export function Header({ className }: { className?: string }) {
  const { logout } = useAuth();
  const user = useAuthUser();
  const hideSupport = useHideHeaderSupport();
  const hideUser = useHideHeaderUser();
  const showSearch = useFeatureFlag('spotlight');
  const showWatchLive = useFeatureFlag('page-watch');
  const showBannerInbox = useFeatureFlag('banner-inbox');
  const bannerInboxMenu = useBannerInboxMenu();

  const logo = { href: PlayerLink, label: m.commons_header_logo_label() } as const;

  const watchLive = showWatchLive
    ? ({
        asChild: true,
        label: (
          <Link to={WatchPaths.watch.path}>
            <span>{m.commons_header_watch_now()}</span>
            <Icon name="play-circle" size="lg" />
          </Link>
        ),
      } as const)
    : undefined;

  if (hideUser) {
    return (
      <SiteHeader
        className={className}
        logo={logo}
        watchLive={watchLive}
        support={hideSupport ? undefined : { url: `https://www.${env.waipuDomain}/hilfe`, label: m.commons_header_support_label() }}
        user={null}
      />
    );
  }

  return (
    <SiteHeader
      className={className}
      logo={logo}
      watchLive={watchLive}
      support={hideSupport ? undefined : { url: `https://www.${env.waipuDomain}/hilfe`, label: m.commons_header_support_label() }}
      bannerInbox={showBannerInbox ? bannerInboxMenu : undefined}
      user={user}
      login={{ label: m.auth_navbar_login(), onClick: () => login() }}
      accountMenu={{
        ariaLabel: m.commons_header_my_waipu(),
        searchPlaceholder: showSearch ? m.commons_header_account_search_placeholder() : undefined,
        items: [{ id: 'logout', label: m.auth_navbar_logout(), icon: 'logout', variant: 'danger', onClick: logout }],
        triggerAriaLabel: m.commons_header_user_label(),
      }}
    />
  );
}
